/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../store/store";
import {
    submitGetCollectSubscription,
    submitCreateCollectSubscription,
    submitResubscribeCollectSubscription,
    submitCancelCollectSubscription,
    submitReactivateCollectSubscription,
    submitUpdateCollectPaymentMethod,
} from "../services/collectBillingService";
import {
    ICancelCollectSubscriptionPayload,
    ICreateCollectSubscriptionPayload,
    IUpdateCollectPaymentMethodPayload,
} from "../../utility/types/collectTypes";

// Resolve the active businessId (mirrors collectAction.ts) so UI can dispatch
// without threading the id through every call site.
function resolveBusinessId(
    getState: () => unknown,
    override?: string,
): string {
    if (override) return override;
    const state = getState() as RootState;
    const id = state.collect?.activeBusinessId;
    if (!id) {
        throw new Error("No active business selected.");
    }
    return id;
}

export const fetchCollectSubscription = createAsyncThunk(
    "collectBilling/fetchSubscription",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitGetCollectSubscription(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load subscription.");
        }
    },
);

export const createCollectSubscription = createAsyncThunk(
    "collectBilling/createSubscription",
    async (
        payload: ICreateCollectSubscriptionPayload,
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            if (!payload.paymentMethodId) {
                throw new Error("A payment method is required.");
            }
            return await submitCreateCollectSubscription(
                id,
                payload.paymentMethodId,
            );
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to start subscription.");
        }
    },
);

export const resubscribeCollectSubscription = createAsyncThunk(
    "collectBilling/resubscribe",
    async (
        payload: ICreateCollectSubscriptionPayload,
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitResubscribeCollectSubscription(
                id,
                payload.paymentMethodId,
            );
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to resubscribe.");
        }
    },
);

export const cancelCollectSubscription = createAsyncThunk(
    "collectBilling/cancelSubscription",
    async (
        payload: ICancelCollectSubscriptionPayload | undefined,
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload?.businessId);
            return await submitCancelCollectSubscription(id, {
                reason: payload?.reason,
                immediate: payload?.immediate,
            });
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to cancel subscription.");
        }
    },
);

export const reactivateCollectSubscription = createAsyncThunk(
    "collectBilling/reactivateSubscription",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitReactivateCollectSubscription(id);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to reactivate subscription.",
            );
        }
    },
);

export const updateCollectPaymentMethod = createAsyncThunk(
    "collectBilling/updatePaymentMethod",
    async (
        payload: IUpdateCollectPaymentMethodPayload,
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitUpdateCollectPaymentMethod(
                id,
                payload.paymentMethodId,
            );
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to update payment method.",
            );
        }
    },
);
